const DEFAULT_TTL_MS = 60_000;

const cacheStore = new Map();
const pendingStore = new Map();

const isPlainObject = (value) => {
        return (
                value !== null &&
                typeof value === "object" &&
                (value.constructor === Object || Object.getPrototypeOf(value) === null)
        );
};

const normalizeForCache = (value) => {
        if (value instanceof Date) {
                return value.getTime();
        }

        if (Array.isArray(value)) {
                return value.map((item) => normalizeForCache(item));
        }

        if (value && typeof value === "object") {
                if (!isPlainObject(value)) {
                        return value;
                }

                const normalized = {};
                Object.keys(value)
                        .sort()
                        .forEach((key) => {
                                const item = value[key];

                                if (typeof item === "function" || item === undefined) {
                                        return;
                                }

                                normalized[key] = normalizeForCache(item);
                        });
                return normalized;
        }

        return value;
};

export const createCacheKey = (namespace, ...args) => {
        const normalizedArgs = args.map((arg) => normalizeForCache(arg));
        return `${namespace}:${JSON.stringify(normalizedArgs)}`;
};

export const fetchWithCache = async (
        cacheKey,
        fetcher,
        { ttlMs = DEFAULT_TTL_MS, forceRefresh = false } = {}
) => {
        const now = Date.now();

        if (!forceRefresh) {
                const cached = cacheStore.get(cacheKey);
                if (cached && cached.expiresAt > now) {
                        return cached.value;
                }

                const pending = pendingStore.get(cacheKey);
                if (pending) {
                        return pending;
                }
        }

        // A `forceRefresh` call replaces the pending entry of an already in-flight
        // normal fetch. Without an ownership check the older (slower) fetch would
        // still write its stale value into the cache when it finally resolves and
        // would delete a pending entry that now belongs to the newer fetch.
        // Each fetch therefore only mutates shared state while it still owns the
        // pending slot; the losing fetch resolves its own caller and nothing else.
        let fetchPromise;
        const ownsPendingSlot = () => pendingStore.get(cacheKey) === fetchPromise;

        fetchPromise = (async () => {
                try {
                        const value = await fetcher();
                        if (ownsPendingSlot()) {
                                cacheStore.set(cacheKey, {
                                        value,
                                        expiresAt: Date.now() + ttlMs,
                                });
                                pendingStore.delete(cacheKey);
                        }
                        return value;
                } catch (error) {
                        if (ownsPendingSlot()) {
                                pendingStore.delete(cacheKey);
                                cacheStore.delete(cacheKey);
                        }
                        throw error;
                }
        })();

        pendingStore.set(cacheKey, fetchPromise);
        return fetchPromise;
};

export const invalidateCache = (prefix) => {
        for (const key of Array.from(cacheStore.keys())) {
                if (key === prefix || key.startsWith(prefix)) {
                        cacheStore.delete(key);
                }
        }

        for (const key of Array.from(pendingStore.keys())) {
                if (key === prefix || key.startsWith(prefix)) {
                        pendingStore.delete(key);
                }
        }
};

export const clearCache = () => {
        cacheStore.clear();
        pendingStore.clear();
};

export const DEFAULT_CACHE_TTL_MS = DEFAULT_TTL_MS;
